'use client';

import { JSX, useMemo } from 'react';
import { Field, ImageField } from '@sitecore-content-sdk/nextjs';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { XceleratePage } from 'src/baseTypes/Xcelerate.HztlFoundation.model';
import { DAY_ORDER, DAY_ABBR_TO_FULL } from 'helpers/Constants/SiteConfig';
import { findComponent } from 'lib/utils/object-utils';
import { Content } from '.generated/Content/Hero.model';
import { usePathname, useSearchParams } from 'next/navigation';
import ClientHead from 'lib/client-head/ClientHead';
import { getCanonicalUrl } from 'lib/seo/getCanonicalUrl';
export type HeroProps = Content.Hero.Hero_Component;

type OfficeFields = {
  officeDisplayName?: Field<string>;
  locationName?: Field<string>;
  canonicalUrl?: Field<string>;
  telephone?: Field<string>;
  image?: ImageField;
  // Expert Profile fields
  firstname?: Field<string>;
  lastname?: Field<string>;
  jobtitle?: Field<string>;
  picture?: ImageField;
  addressFirstLine?: Field<string>;
  addressSecondLine?: Field<string>;
  addressThirdLine?: Field<string>;
  city?: Field<string>;
  postCode?: Field<string>;
  country?: Field<string>;
  openingHours?: Field<string>;
};

type KeyContactsProps = {
  displayName?: string;
  url?: string;
};

type ArticleFields = {
  heading?: Field<string>;
  subHeading?: Field<string>;
  description?: Field<string>;
  image?: ImageField;
  keyContact?: KeyContactsProps;
  authors?: Array<
    | KeyContactsProps
    | { url?: string; fields?: { firstname?: Field<string>; lastname?: Field<string> } }
  >;
  date?: Field<string>;
  pageTitle?: Field<string>;
  MetaDescription?: Field<string>;
  pageDescription?: Field<string>;
};

type OpeningHoursSpec = {
  '@type': 'OpeningHoursSpecification';
  dayOfWeek?: string[];
  opens?: string;
  closes?: string;
};

const normalizeDayToken = (token: string): string | null => {
  const t = token.trim();
  if (!t) return null;
  const cap = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  if (DAY_ORDER.includes(cap)) return cap;
  const clean = cap.replace(/\.$/, '');
  return DAY_ABBR_TO_FULL[clean as keyof typeof DAY_ABBR_TO_FULL] || null;
};

const expandDayRange = (start: string, end: string): string[] => {
  const sIdx = DAY_ORDER.indexOf(start);
  const eIdx = DAY_ORDER.indexOf(end);
  if (sIdx === -1 || eIdx === -1) return [];
  if (sIdx <= eIdx) return DAY_ORDER.slice(sIdx, eIdx + 1);
  // Handle wrap-around ranges if ever used (e.g., Sun-Tue)
  return [...DAY_ORDER.slice(sIdx), ...DAY_ORDER.slice(0, eIdx + 1)];
};

const expandDaysExpression = (expr: string): string[] => {
  // Support expressions like "Mon-Fri", "Monday-Friday", "Sat,Sun", "Mon, Tue, Wed"
  const normalized = expr.replace(/\band\b/gi, ',');
  const parts = normalized
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const result: string[] = [];
  for (const p of parts) {
    const rangeMatch = p.split(/[-–]/).map((p) => p.trim());
    if (rangeMatch) {
      const start = normalizeDayToken(rangeMatch[0] || '');
      const end = normalizeDayToken(rangeMatch[1] || '');
      if (start && end) {
        if (rangeMatch[2]) {
          result.push(...expandDayRange(start, end), rangeMatch[2]);
        } else {
          result.push(...expandDayRange(start, end));
        }
      }
    }
  }
  return result;
};

const parseOpeningHoursString = (raw: string): OpeningHoursSpec[] => {
  // Split by common separators: semicolons, pipes, newlines
  const segments = raw
    .split(/[;|\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const specs: OpeningHoursSpec[] = [];
  for (const seg of segments) {
    const m = seg.split(':');
    if (!m) continue;
    const timeParts = m[1]?.split(/[-–—]/);
    const opens = timeParts?.[0];
    const closes = timeParts?.[1];
    const dayPart = m[0].replace(/[:.-]+\s*$/, '').trim();
    if (!dayPart) continue;
    const days = expandDaysExpression(dayPart);
    if (days.length === 0) continue;
    specs.push({ '@type': 'OpeningHoursSpecification', dayOfWeek: days, opens, closes });
  }
  return specs;
};

const getExpertProfileUrl = (currentUrl: string) => {
  // Remove single letter slug pattern: /our-people/[a-z]/[slug] -> /our-people/[slug]
  if (currentUrl.includes('/our-people/')) {
    // Match pattern: /our-people/[single-letter-a-z]/[rest-of-path]
    const pattern = /\/our-people\/([a-z])\/(.+)/;
    const match = currentUrl.match(pattern);
    if (match) {
      // Return /our-people/[rest-of-path]
      return process.env.PUBLIC_URL + `/our-people/${match[2]}`;
    }
  }
  return currentUrl;
};

const Schema = (): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const currentPage = sitecoreContext?.route as XceleratePage & {
    fields?: OfficeFields & { schemaMarkup?: Field<string> };
    templateId?: string;
    placeholders?: {
      'headless-header'?: {
        placeholders?: {
          'sxa-header'?: {
            fields?: { logo: ImageField };
          }[];
        };
      }[];
    };
  };
  const { schemaMarkup } = currentPage?.fields ?? {};
  const {
    heroTitle,
    heroDescription,
    heroImage,
    officeDisplayName,
    locationName,
    canonicalUrl,
    telephone,
    firstname,
    lastname,
    jobtitle,
    picture,
    addressFirstLine,
    addressSecondLine,
    addressThirdLine,
    city,
    postCode,
    country,
    openingHours,
  } = currentPage?.fields ?? {};

  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const asPath = `${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`;

  const resolvedCanonicalUrl = useMemo(
    () =>
      getCanonicalUrl({
        explicitUrl: canonicalUrl?.value,
        itemPath: asPath,
      }),
    [canonicalUrl?.value, asPath]
  );

  // Validate if schemaMarkup.value is a valid JSON string
  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const toISODate = (dateString?: string): string | undefined => {
    if (!dateString) return undefined;
    if (/^\d{8}T\d{6}/.test(dateString)) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  };

  const isHeroContent = findComponent(sitecoreContext, 'Hero');
  const heroFields = isHeroContent?.[0]?.fields as unknown as NonNullable<HeroProps['fields']>;
  const { image, title, description } = heroFields || {};

  // Build Office Detail Page JSON-LD if applicable
  const templateName = currentPage?.templateId as string;
  let officeSchema: Record<string, unknown> | null = null;
  if (templateName === 'fb3e8ab7-94d1-4da9-93e9-90561a4896d1') {
    const officeName =
      officeDisplayName?.value || locationName?.value || sitecoreContext?.site?.name;
    const officeCanonicalUrl = resolvedCanonicalUrl;
    const officeTelephone = telephone?.value;
    // Defensive access for deeply nested placeholders chains;
    const imageUrl = heroImage?.value?.src || image?.value?.src;
    const streetAddress = [
      addressFirstLine?.value,
      addressSecondLine?.value,
      addressThirdLine?.value,
    ]
      .filter(Boolean)
      .join(', ');
    const addressLocality = city?.value;
    const postalCode = postCode?.value;
    const addressCountry = country?.value;

    let openingHoursSpecification: OpeningHoursSpec[] | undefined;
    const openingHoursRaw: string = (openingHours?.value as unknown as string) || '';
    if (openingHoursRaw) {
      if (isValidJson(openingHoursRaw)) {
        try {
          const parsed = JSON.parse(openingHoursRaw) as unknown;
          if (Array.isArray(parsed)) {
            openingHoursSpecification = parsed as OpeningHoursSpec[];
          }
        } catch {
          // fall back to string parsing
        }
      }
      if (!openingHoursSpecification || openingHoursSpecification.length === 0) {
        const parsedFromString = parseOpeningHoursString(openingHoursRaw);
        if (parsedFromString.length > 0) openingHoursSpecification = parsedFromString;
      }
    }

    officeSchema = {
      '@context': 'https://schema.org',
      '@type': 'LegalService',
      name: officeName,
      ...(imageUrl ? { image: imageUrl } : {}),
      ...(officeCanonicalUrl ? { '@id': officeCanonicalUrl, url: officeCanonicalUrl } : {}),
      ...(officeTelephone ? { telephone: officeTelephone } : {}),
      address: {
        '@type': 'PostalAddress',
        ...(streetAddress ? { streetAddress } : {}),
        ...(addressLocality ? { addressLocality } : {}),
        ...(postalCode ? { postalCode } : {}),
        ...(addressCountry ? { addressCountry } : {}),
      },
      ...(openingHoursSpecification ? { openingHoursSpecification } : {}),
    } as Record<string, unknown>;
  }

  // Build Expert Profile (Person) JSON-LD if applicable
  let personSchema: Record<string, unknown> | null = null;
  if (templateName === 'e0ca334d-0e3d-4d41-9447-2b694b072fd7') {
    const givenName = firstname?.value?.trim();
    const familyName = lastname?.value?.trim();
    const fullName = [givenName, familyName].filter(Boolean).join(' ');
    const personName = fullName || heroTitle?.value || title?.value;
    const profileUrl = getExpertProfileUrl(resolvedCanonicalUrl);
    const profileImage = picture?.value?.src || heroImage?.value?.src || image?.value?.src;
    const personJobTitle = jobtitle?.value;

    personSchema = {
      '@context': 'https://schema.org/',
      '@type': 'Person',
      name: personName,
      ...(profileUrl ? { url: profileUrl } : {}),
      ...(profileImage ? { image: profileImage } : {}),
      ...(personJobTitle ? { jobTitle: personJobTitle } : {}),
      worksFor: {
        '@type': 'Organization',
        name: sitecoreContext?.site?.name,
      },
    } as Record<string, unknown>;
  }

  function capitalizeEachWord(sentence: string): string {
    // Split the sentence into an array of words
    const words = sentence.split('-');

    // Iterate over each word and capitalize its first letter
    for (let i = 0; i < words.length; i++) {
      if (words[i].length > 0) {
        // Check if the word is not empty
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
      }
    }

    // Join the words back into a sentence
    return words.join(' ');
  }

  // Build Article (Article Detail Page) JSON-LD if applicable
  let articleSchema: Record<string, unknown> | null = null;
  if (
    templateName === '150f5c35-f575-4192-88b3-c24306edbb01' ||
    templateName === 'bd73bac7-a401-4913-a42d-15238bba6baa'
  ) {
    const articleFields = (currentPage?.fields ?? {}) as ArticleFields;
    const canonical = resolvedCanonicalUrl;
    const headline =
      articleFields.heading?.value ||
      heroTitle?.value ||
      title?.value ||
      articleFields?.pageTitle?.value ||
      undefined;
    const descriptionText =
      articleFields.description?.value ||
      description?.value ||
      heroDescription?.value ||
      articleFields?.MetaDescription?.value ||
      articleFields?.pageDescription?.value ||
      undefined;
    const imageUrl =
      articleFields.image?.value?.src || heroImage?.value?.src || image?.value?.src || undefined;
    const authorNameValue =
      capitalizeEachWord(articleFields?.keyContact?.displayName || '') || undefined;
    let authorUrl: string | undefined;

    if (
      typeof process !== 'undefined' &&
      typeof process.env !== 'undefined' &&
      typeof process.env.PUBLIC_URL === 'string' &&
      process.env.PUBLIC_URL &&
      articleFields?.keyContact?.url
    ) {
      authorUrl = process.env.PUBLIC_URL + articleFields.keyContact.url;
    } else if (articleFields?.keyContact?.url) {
      authorUrl = articleFields.keyContact.url;
    } else {
      authorUrl = undefined;
    }

    type AuthorEntry =
      | KeyContactsProps
      | {
          url?: string | { path?: string };
          fields?: { firstname?: Field<string>; lastname?: Field<string> };
        };
    const getAuthorName = (author: AuthorEntry) => {
      if (!author) return undefined;
      if ('displayName' in author && author.displayName) {
        return capitalizeEachWord(author.displayName) || undefined;
      }
      if ('fields' in author && author.fields) {
        const first = (author.fields.firstname?.value ?? '').trim();
        const last = (author.fields.lastname?.value ?? '').trim();
        return [first, last].filter(Boolean).join(' ') || undefined;
      }
      return undefined;
    };
    const getAuthorUrl = (author: AuthorEntry) => {
      if (!author || !('url' in author) || author.url == null) return '';
      const u = author.url;
      const urlStr = typeof u === 'string' ? u : ((u as { path?: string })?.path ?? '');
      if (
        typeof process !== 'undefined' &&
        typeof process.env !== 'undefined' &&
        typeof process.env.PUBLIC_URL === 'string' &&
        process.env.PUBLIC_URL &&
        urlStr.startsWith('/')
      ) {
        return process.env.PUBLIC_URL + urlStr;
      }
      return urlStr;
    };

    const authorEntries =
      articleFields.authors && articleFields.authors.length > 0
        ? articleFields.authors
            .map((a) => {
              const name = getAuthorName(a);
              const url = getAuthorUrl(a);
              if (!name) return null;
              return { '@type': 'Person' as const, name, url: getExpertProfileUrl(url || '') };
            })
            .filter((x): x is { '@type': 'Person'; name: string; url: string } => x !== null)
        : authorNameValue
          ? [
              {
                '@type': 'Person' as const,
                name: authorNameValue,
                url: getExpertProfileUrl(authorUrl || ''),
              },
            ]
          : [];

    const datePublished = toISODate(articleFields.date?.value);

    const isHeaderContent = findComponent(sitecoreContext, 'Header');
    const logoUrl = (isHeaderContent?.[0]?.fields?.logo as ImageField)?.value?.src || undefined;

    articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      ...(canonical ? { mainEntityOfPage: { '@type': 'WebPage', '@id': canonical } } : {}),
      ...(headline ? { headline } : {}),
      ...(descriptionText ? { description: descriptionText } : {}),
      ...(imageUrl ? { image: imageUrl } : {}),
      ...(authorEntries.length > 0
        ? {
            author: authorEntries.length === 1 ? authorEntries[0] : authorEntries,
          }
        : {}),
      publisher: {
        '@type': 'Organization',
        name: sitecoreContext?.site?.name,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
        },
      },
      ...(datePublished ? { datePublished } : {}),
    } as Record<string, unknown>;
  }

  return (
    <ClientHead>
      {[
        schemaMarkup?.value && isValidJson(schemaMarkup.value)
          ? JSON.parse(schemaMarkup.value)
          : null,
        officeSchema,
        personSchema,
        articleSchema,
      ]
        .filter(Boolean)
        .map((schema, idx) => (
          <script
            key={idx}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
            }}
          />
        ))}
    </ClientHead>
  );
};

export default Schema;
